import { motion } from 'framer-motion';
import { Phone, MapPin, Mail, Facebook, Instagram, Youtube } from 'lucide-react';
import { Logo } from '@/components/Logo';

const footerLinks = {
  services: [
    { name: 'Painting Services', href: '#services' },
    { name: 'Electrical Services', href: '#services' },
    { name: 'Plumbing Services', href: '#services' },
    { name: 'Complete Construction', href: '#services' },
    { name: 'Home Renovation', href: '#services' },
    { name: 'Smart Home Solutions', href: '#services' },
    { name: 'Renovation Works', href: '#services' },
    { name: 'Safety Inspections', href: '#services' },
    { name: 'Interior Design', href: '#services' },
    { name: 'Aluminium Work', href: '#services' },
    { name: 'CPVC Work', href: '#services' },
    { name: 'Flooring — Tiles, Granite', href: '#services' },
  ],
  company: [
    { name: 'About Us', href: '#about' },
    { name: 'Our Team', href: '#team' },
    { name: 'Our Work', href: '#gallery' },
    { name: 'Testimonials', href: '#testimonials' },
    { name: 'Our Partners', href: '#partners' },
    { name: 'Contact', href: '#contact' },
  ],
};

const socialLinks = [
  { icon: Facebook, href: '#', label: 'Facebook' },
  { icon: Instagram, href: 'https://www.instagram.com/sagar_ventures_official', label: 'Instagram' },
  { icon: Youtube, href: 'https://www.youtube.com/@SAGARVENTURES-i7y6h', label: 'YouTube' },
];

export const Footer = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-secondary text-secondary-foreground">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              {/* Logo */}
              <div className="mb-6">
                <div className="bg-white rounded-lg p-2 inline-block">
                  <Logo className="h-16 w-auto" />
                </div>
              </div>

              <p className="text-secondary-foreground/80 mb-6 text-sm leading-relaxed">
                Transforming homes with expert care. Your trusted partner for painting, 
                electrical, plumbing, and complete construction services in Arkalgud.
              </p>

              {/* Social Links */}
              <div className="flex gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target={social.href.startsWith('http') ? '_blank' : undefined}
                    rel={social.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    aria-label={social.label}
                    className="w-10 h-10 rounded-full bg-secondary-foreground/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Services Column */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h4 className="font-display text-lg font-semibold mb-5">
                Our Services
              </h4>
              <ul className="space-y-3">
                {footerLinks.services.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(link.href);
                      }}
                      className="text-secondary-foreground/80 hover:text-primary transition-colors text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Company Column */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h4 className="font-display text-lg font-semibold mb-5">
                Quick Links
              </h4>
              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(link.href);
                      }}
                      className="text-secondary-foreground/80 hover:text-primary transition-colors text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Contact Column */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <h4 className="font-display text-lg font-semibold mb-5">
                Contact Us
              </h4>
              <ul className="space-y-4">
                <li>
                  <a
                    href="tel:8618759831"
                    className="flex items-start gap-3 text-secondary-foreground/80 hover:text-primary transition-colors"
                  >
                    <Phone className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                    <span className="text-sm">8618759831</span>
                  </a>
                </li>
                <li>
                  <a
                    href="https://maps.google.com/?q=Arkalgud"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 text-secondary-foreground/80 hover:text-primary transition-colors"
                  >
                    <MapPin className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                    <span className="text-sm">By Paas Road, Arkalgud, Karnataka</span>
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:info@sagarventures.com"
                    className="flex items-start gap-3 text-secondary-foreground/80 hover:text-primary transition-colors"
                  >
                    <Mail className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                    <span className="text-sm">info@sagarventures.com</span>
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.youtube.com/@SAGARVENTURES-i7y6h"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 text-secondary-foreground/80 hover:text-primary transition-colors"
                  >
                    <Youtube className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                    <span className="text-sm">YouTube Channel</span>
                  </a>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-secondary-foreground/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-secondary-foreground/60">
              © {new Date().getFullYear()} Sagar Ventures. All rights reserved.
            </p>
            <div className="flex flex-col md:flex-row items-center gap-4">
              <p className="text-sm text-secondary-foreground/60">
                Trusted by Homeowners of Arkalgud
              </p>
              <p className="text-sm text-secondary-foreground/60">
                Crafted by{' '}
                <a
                  href="https://nexhala.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-light transition-colors underline"
                >
                  Nexhala
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
